import { combineReducers } from 'redux';

import type { ApiError } from 'modules/global/generic.types';
import error from 'modules/global/reducers/generic.error';
import loading from 'modules/global/reducers/generic.loading';
import { isSuccessRequest } from 'modules/global/reducers/utils';
import * as actionTypes from 'modules/metrics/actionTypes';
import {
  BILLING_TYPE,
  DOCUMENT_VALIDATION_TYPE,
  type BillingEntityEnum,
  type DocumentValidationTypeEnum,
} from 'modules/metrics/constants';
import filters, { type EntityMatcherConfig, type MetricsFiltersReducer } from 'modules/metrics/reducers/filters';
import type { SetDocumentsTabAction } from 'modules/metrics/types/actions';
import type { WrappedBilling } from 'modules/metrics/types/billing';
import { createReducer } from 'utils/ReduxUtils';

export type MetricsReducerTypeSlice = {
  fetching: boolean;
  apiError: ApiError | null;
  data: WrappedBilling[];
  filters: MetricsFiltersReducer;
};

/** Every channel gets a slice, and so does each document variant: they are fetched separately. */
const ENTITIES: BillingEntityEnum[] = [...Object.values(BILLING_TYPE), ...Object.values(DOCUMENT_VALIDATION_TYPE)];

/** Every request of this module carries the entity it belongs to in its meta. */
const entityMatcher = (entity: BillingEntityEnum): EntityMatcherConfig => ({
  matcher: ({ meta }) => meta?.entity === entity,
});

const entitySlice = (entity: BillingEntityEnum) => {
  const global = entityMatcher(entity);

  return combineReducers({
    data: createReducer<WrappedBilling[]>([], {}, [
      {
        matcher: isSuccessRequest(actionTypes.FETCH_BILLING_ENTITY_METRICS_REQUEST, global),
        reducer: (_, { payload }: { payload: WrappedBilling[] }) => payload,
      },
    ]),
    filters: filters(entity, global),
    apiError: error(actionTypes.FETCH_BILLING_ENTITY_METRICS_REQUEST, { global }),
    fetching: loading(actionTypes.FETCH_BILLING_ENTITY_METRICS_REQUEST, { global }),
  });
};

const metrics = combineReducers({
  exporting: loading(actionTypes.EXPORT_BILLING_REQUEST),
  selectedDocumentsTab: createReducer<DocumentValidationTypeEnum>(DOCUMENT_VALIDATION_TYPE.DOCUMENT_ONLY, {
    [actionTypes.SET_DOCUMENTS_TAB]: (_, { payload }: SetDocumentsTabAction) => payload,
  }),
  ...ENTITIES.reduce(
    (acc, entity) => ({ ...acc, [entity]: entitySlice(entity) }),
    {} as Record<BillingEntityEnum, ReturnType<typeof entitySlice>>
  ),
});

export type MetricsReducer = ReturnType<typeof metrics>;

export default metrics;
